import { Controller, type UseFormReturn } from 'react-hook-form';
import { SectionCard } from '@/components/common/SectionCard';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { TextField } from '@/components/forms/TextField';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { LeadInvestorFields } from '../LeadInvestorFields';
import type { CreateStartupFormValues } from '../../schema';
import {
  CREATE_STARTUP_STRINGS,
  FIELD_HELPERS,
  FIELD_LABELS,
  FIELD_PLACEHOLDERS,
  INSTRUMENT_SEGMENT_OPTIONS,
  SECTION_TITLES,
} from '../../constants';

interface StepProps {
  form: UseFormReturn<CreateStartupFormValues>;
}

/** Step 3 "Current Raise" (Figma 197:685). */
export function Step3CurrentRaise({ form }: StepProps) {
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = form;

  return (
    <SectionCard title={SECTION_TITLES.CURRENT_RAISE} subtitle={CREATE_STARTUP_STRINGS.RAISE_SUBTITLE}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <FieldLabel label={FIELD_LABELS.raiseRange} variant="caps" />
          <div className="grid gap-5 sm:grid-cols-2">
            <Controller
              control={control}
              name="financeSoughtLow"
              render={({ field }) => (
                <TextField
                  {...field}
                  numeric
                  label={FIELD_LABELS.financeSoughtLow}
                  labelVariant="caps"
                  prefix="$"
                  placeholder={FIELD_PLACEHOLDERS.money}
                  error={errors.financeSoughtLow?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="financeSoughtHigh"
              render={({ field }) => (
                <TextField
                  {...field}
                  numeric
                  label={FIELD_LABELS.financeSoughtHigh}
                  labelVariant="caps"
                  prefix="$"
                  placeholder={FIELD_PLACEHOLDERS.money}
                  error={errors.financeSoughtHigh?.message}
                />
              )}
            />
          </div>
          <p className="text-xs text-slate">{FIELD_HELPERS.raiseRange}</p>
        </div>

        <div className="flex flex-col gap-2">
          <FieldLabel label={FIELD_LABELS.instrument} variant="caps" />
          <Controller
            control={control}
            name="instrument"
            render={({ field }) => (
              <SegmentedControl
                label={FIELD_LABELS.instrument}
                options={INSTRUMENT_SEGMENT_OPTIONS}
                value={field.value}
                onChange={field.onChange}
                error={errors.instrument?.message}
              />
            )}
          />
        </div>

        <TextField
          {...register('targetCloseDate')}
          label={FIELD_LABELS.targetCloseDate}
          labelVariant="caps"
          type="date"
          placeholder={FIELD_PLACEHOLDERS.targetCloseDate}
          // Native date inputs only open from the tiny indicator; open the
          // picker from anywhere in the field like the Figma dropdown.
          onClick={(event) => {
            try {
              event.currentTarget.showPicker?.();
            } catch {
              // Non-gesture or unsupported browser - the indicator still works.
            }
          }}
          error={errors.targetCloseDate?.message}
        />

        <Controller
          control={control}
          name="valuationOrCap"
          render={({ field }) => (
            <TextField
              {...field}
              numeric
              label={FIELD_LABELS.valuationOrCap}
              labelVariant="caps"
              prefix="$"
              placeholder={FIELD_PLACEHOLDERS.money}
              helpText={FIELD_HELPERS.valuationOrCap}
              error={errors.valuationOrCap?.message}
            />
          )}
        />

        <LeadInvestorFields
          hasLeadInvestor={watch('hasLeadInvestor')}
          onHasLeadInvestorChange={(next) =>
            setValue('hasLeadInvestor', next, { shouldDirty: true })
          }
          nameInputProps={register('leadInvestorName')}
          nameError={errors.leadInvestorName?.message}
        />
      </div>
    </SectionCard>
  );
}
